import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { countryCodes, selectOptions } from '../../utils/validators';
import '../../components/public/Public.css';
import './Register.css';

const STEPS = ['Email', 'Verify', 'Details', 'Confirm'];

export default function Register() {
  const { slug }  = useParams();
  const navigate  = useNavigate();
  const { showToast } = useToast();

  const [event, setEvent]     = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [step, setStep]       = useState(0);
  const [busy, setBusy]       = useState(false);

  // email step
  const [email, setEmail]     = useState('');
  const [emailExists, setEmailExists] = useState(false);

  // OTP step
  const [otp, setOtp]         = useState('');
  const [resendIn, setResendIn] = useState(0);

  // details
  const [form, setForm] = useState({
    firstname:'', lastname:'', country_code:'+233', phone:'', country:'Ghana', city:'',
    role_label:'', industry:'', occupation:'', ai_experience_level:'', heard_about_us:'', hope_to_learn:'',
    session_id:'',
  });
  const [errors, setErrors] = useState({});

  /* ── Load event ──────────────────────────────────────── */
  useEffect(() => {
    api.get(`/public/events/${slug}`)
      .then(r => {
        setEvent(r.data);
        if (r.data.sessions?.length === 1) {
          setForm(f => ({ ...f, session_id: String(r.data.sessions[0].id) }));
        }
      })
      .catch(() => showToast('Event not found', 'error'))
      .finally(() => setLoadingEvent(false));
  }, [slug, showToast]);

  /* ── Resend countdown ────────────────────────────────── */
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ── Send a verification code (no name needed) ───────── */
  const sendOtp = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post('/public/send-otp', { email });
      if (r.data.emailSent === false) {
        showToast(r.data.devOtp ? `Email not set up yet — your code is ${r.data.devOtp}` : r.data.message, 'warning', 9000);
      } else {
        showToast('Verification code sent to your email.', 'success');
      }
      setResendIn(45);
      return true;
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send code. Try again.', 'error');
      return false;
    } finally {
      setBusy(false);
    }
  }, [email, showToast]);

  /* ── Step 0: check email, then send code immediately ─── */
  const handleCheckEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: 'Enter a valid email address.' });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const r = await api.post('/public/check-email', { email });
      if (r.data.exists && r.data.verified) {
        // Returning verified user → skip OTP entirely, go to details
        setEmailExists(true);
        if (r.data.firstname) setForm(f => ({ ...f, firstname: r.data.firstname }));
        setStep(2);
        showToast(`Welcome back${r.data.firstname ? ', ' + r.data.firstname : ''}!`, 'success');
        return;
      }
      // New / unverified → send the code now and go straight to the verify step
      setEmailExists(false);
      if (r.data.firstname) setForm(f => ({ ...f, firstname: r.data.firstname }));
      const sent = await sendOtp();
      if (sent) setStep(1);
    } catch {
      showToast('Could not verify email. Try again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setErrors({ otp: 'Enter the 6-digit code.' });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await api.post('/public/verify-otp', { email, otp });
      showToast('Email verified!', 'success');
      setStep(2);
    } catch (err) {
      setErrors({ otp: err.response?.data?.error || 'Invalid or expired code.' });
    } finally {
      setBusy(false);
    }
  };

  /* ── Step 3: validate details ────────────────────────── */
  const validateDetails = () => {
    const e = {};
    if (!form.firstname.trim()) e.firstname = 'First name required.';
    if (!form.lastname.trim())  e.lastname  = 'Last name required.';
    if (!/^\d{7,15}$/.test(form.phone.replace(/\s/g,''))) e.phone = 'Valid phone number required.';
    if (!form.country.trim())   e.country   = 'Country required.';
    if (!form.city.trim())      e.city      = 'City required.';
    if (!form.session_id)       e.session_id = 'Please select a session.';
    // For new users we ask the richer profile fields
    if (!emailExists) {
      if (!form.role_label)          e.role_label          = 'Please select a role.';
      if (!form.industry)            e.industry            = 'Please select an industry.';
      if (!form.ai_experience_level) e.ai_experience_level = 'Please select your level.';
      if (!form.heard_about_us)      e.heard_about_us      = 'Please let us know.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Step 4: submit registration ─────────────────────── */
  const handleRegister = async () => {
    if (!validateDetails()) { showToast('Please fix the errors below', 'warning'); return; }
    setBusy(true);
    try {
      const payload = {
        email,
        firstname: form.firstname.trim(),
        lastname:  form.lastname.trim(),
        phone:     form.phone.replace(/\s/g,''),
        country_code: form.country_code,
        country:   form.country,
        city:      form.city,
        role_label: form.role_label,
        industry:  form.industry,
        occupation: form.occupation,
        ai_experience_level: form.ai_experience_level,
        heard_about_us: form.heard_about_us,
        hope_to_learn: form.hope_to_learn,
        event_slug: slug,
        session_id: parseInt(form.session_id),
      };
      const r = await api.post('/public/register', payload);
      if (r.data.requiresPayment) {
        // go to payment page with txn ref
        navigate(`/payment/${r.data.transactionRef}`, {
          state: { amount: r.data.amount, currency: r.data.currency, eventTitle: r.data.eventTitle, phone: `${form.country_code}${form.phone.replace(/^0+/,'')}` }
        });
      } else {
        setStep(3); // show success confirmation for free events
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Registration failed. Try again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loadingEvent) return (
    <div className="pub-page"><Navbar /><div className="pub-loading"><div className="spinner" /></div><Footer /></div>
  );

  const isFreeConfirm = step === 3 && event?.is_free;

  return (
    <div className="pub-page">
      <Navbar />

      <div className="reg-wrap">
        <div className="reg-container">
          {/* Event summary header */}
          {event && (
            <div className="reg-event-head">
              <div className="reg-event-thumb">
                {event.image_url
                  ? <img src={event.image_url} alt={event.title} />
                  : <div className="reg-thumb-ph">{event.title?.[0]}</div>}
              </div>
              <div>
                <div className="reg-event-label">Registering for</div>
                <h1 className="reg-event-title">{event.title}</h1>
                <div className="reg-event-price">
                  {event.is_free ? <span className="free">Free Event</span> : <span className="paid">{event.currency} {parseFloat(event.price).toFixed(2)}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Stepper */}
          <div className="reg-stepper">
            {STEPS.map((label, i) => (
              <div key={label} className={`reg-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                <div className="reg-step-dot">{i < step ? '✓' : i + 1}</div>
                <span className="reg-step-label">{label}</span>
                {i < STEPS.length - 1 && <div className="reg-step-line" />}
              </div>
            ))}
          </div>

          {/* Card */}
          <div className="reg-card">

            {/* STEP 0 — Email */}
            {step === 0 && (
              <div className="reg-pane">
                <h2>Let's get started</h2>
                <p className="reg-sub">Enter your email address and we'll send you a verification code.</p>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    className={`form-control form-control-lg ${errors.email ? 'is-invalid' : ''}`}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCheckEmail()}
                    placeholder="you@example.com"
                    autoFocus
                  />
                  {errors.email && <span className="invalid-feedback">{errors.email}</span>}
                </div>
                <button className="pub-btn pub-btn-primary reg-btn" onClick={handleCheckEmail} disabled={busy}>
                  {busy ? <span className="spinner spinner-sm" /> : 'Continue →'}
                </button>
              </div>
            )}

            {/* STEP 1 — Verify (new users) */}
            {step === 1 && (
              <div className="reg-pane">
                <h2>Check your inbox</h2>
                <p className="reg-sub">
                  We've sent a 6-digit verification code to <strong>{email}</strong>. Enter it below to continue.
                </p>

                <div className="form-group">
                  <label className="form-label">Verification Code</label>
                  <input
                    className={`form-control form-control-lg reg-otp-input ${errors.otp ? 'is-invalid' : ''}`}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                    onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                    placeholder="------"
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                  />
                  {errors.otp && <span className="invalid-feedback">{errors.otp}</span>}
                </div>
                <div className="reg-resend">
                  {resendIn > 0
                    ? <span className="text-muted">Resend code in {resendIn}s</span>
                    : <button className="reg-link-btn" onClick={sendOtp} disabled={busy}>Resend code</button>}
                </div>

                <div className="reg-actions">
                  <button className="pub-btn pub-btn-ghost reg-btn-ghost" onClick={() => { setStep(0); setOtp(''); }}>← Back</button>
                  <button className="pub-btn pub-btn-primary reg-btn" onClick={handleVerifyOtp} disabled={busy}>
                    {busy ? <span className="spinner spinner-sm" /> : 'Verify & Continue →'}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2 — Details */}
            {step === 2 && (
              <div className="reg-pane">
                <h2>{emailExists ? 'Confirm your details' : 'Tell us about yourself'}</h2>
                <p className="reg-sub">
                  {emailExists ? 'Quickly confirm your info and pick a session.' : 'Help us tailor the event experience for you.'}
                </p>

                {/* Session selection */}
                {(event?.sessions?.length || 0) > 0 && (
                  <div className="form-group">
                    <label className="form-label">Select Session <span className="req">*</span></label>
                    <div className="reg-session-options">
                      {event.sessions.map(s => (
                        <label key={s.id} className={`reg-session-opt ${String(form.session_id) === String(s.id) ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="session"
                            value={s.id}
                            checked={String(form.session_id) === String(s.id)}
                            onChange={e => set('session_id', e.target.value)}
                          />
                          <div>
                            {s.session_label && <div className="reg-session-opt-label">{s.session_label}</div>}
                            <div className="reg-session-opt-date">
                              {new Date(s.start_date).toLocaleDateString('en-GB',{ weekday:'short', day:'numeric', month:'short', year:'numeric' })}
                              {s.end_date && new Date(s.end_date).toDateString() !== new Date(s.start_date).toDateString()
                                ? ` → ${new Date(s.end_date).toLocaleDateString('en-GB',{ day:'numeric', month:'short', year:'numeric' })}` : ''}
                            </div>
                            {(s.start_time || s.end_time) && (
                              <div className="reg-session-opt-time">{s.start_time?.slice(0,5)}{s.end_time ? ` – ${s.end_time.slice(0,5)}` : ''}</div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    {errors.session_id && <span className="invalid-feedback">{errors.session_id}</span>}
                  </div>
                )}

                <div className="reg-form-grid">
                  <div className="form-group">
                    <label className="form-label">First Name <span className="req">*</span></label>
                    <input className={`form-control ${errors.firstname ? 'is-invalid' : ''}`} value={form.firstname} onChange={e => set('firstname', e.target.value)} />
                    {errors.firstname && <span className="invalid-feedback">{errors.firstname}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name <span className="req">*</span></label>
                    <input className={`form-control ${errors.lastname ? 'is-invalid' : ''}`} value={form.lastname} onChange={e => set('lastname', e.target.value)} />
                    {errors.lastname && <span className="invalid-feedback">{errors.lastname}</span>}
                  </div>
                </div>

                <div className="reg-form-grid">
                  <div className="form-group">
                    <label className="form-label">Phone <span className="req">*</span></label>
                    <div className="reg-phone-group">
                      <select className="form-control reg-cc" value={form.country_code} onChange={e => set('country_code', e.target.value)}>
                        {countryCodes.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                      </select>
                      <input className={`form-control ${errors.phone ? 'is-invalid' : ''}`} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="241234567" />
                    </div>
                    {errors.phone && <span className="invalid-feedback">{errors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">City <span className="req">*</span></label>
                    <input className={`form-control ${errors.city ? 'is-invalid' : ''}`} value={form.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Accra" />
                    {errors.city && <span className="invalid-feedback">{errors.city}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Country <span className="req">*</span></label>
                  <input className={`form-control ${errors.country ? 'is-invalid' : ''}`} value={form.country} onChange={e => set('country', e.target.value)} placeholder="e.g. Ghana" />
                  {errors.country && <span className="invalid-feedback">{errors.country}</span>}
                </div>

                {/* Rich profile fields only for new users */}
                {!emailExists && (
                  <>
                    <div className="reg-form-grid">
                      <div className="form-group">
                        <label className="form-label">Role <span className="req">*</span></label>
                        <select className={`form-control ${errors.role_label ? 'is-invalid' : ''}`} value={form.role_label} onChange={e => set('role_label', e.target.value)}>
                          <option value="">— Select —</option>
                          {selectOptions.roles.map(o => <option key={o}>{o}</option>)}
                        </select>
                        {errors.role_label && <span className="invalid-feedback">{errors.role_label}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Industry <span className="req">*</span></label>
                        <select className={`form-control ${errors.industry ? 'is-invalid' : ''}`} value={form.industry} onChange={e => set('industry', e.target.value)}>
                          <option value="">— Select —</option>
                          {selectOptions.industries.map(o => <option key={o}>{o}</option>)}
                        </select>
                        {errors.industry && <span className="invalid-feedback">{errors.industry}</span>}
                      </div>
                    </div>

                    <div className="reg-form-grid">
                      <div className="form-group">
                        <label className="form-label">Occupation</label>
                        <select className="form-control" value={form.occupation} onChange={e => set('occupation', e.target.value)}>
                          <option value="">— Select —</option>
                          {selectOptions.occupations.map(o => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">AI Experience <span className="req">*</span></label>
                        <select className={`form-control ${errors.ai_experience_level ? 'is-invalid' : ''}`} value={form.ai_experience_level} onChange={e => set('ai_experience_level', e.target.value)}>
                          <option value="">— Select —</option>
                          {selectOptions.aiExperienceLevels.map(o => <option key={o}>{o}</option>)}
                        </select>
                        {errors.ai_experience_level && <span className="invalid-feedback">{errors.ai_experience_level}</span>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">How did you hear about us? <span className="req">*</span></label>
                      <select className={`form-control ${errors.heard_about_us ? 'is-invalid' : ''}`} value={form.heard_about_us} onChange={e => set('heard_about_us', e.target.value)}>
                        <option value="">— Select —</option>
                        {selectOptions.heardAboutUs.map(o => <option key={o}>{o}</option>)}
                      </select>
                      {errors.heard_about_us && <span className="invalid-feedback">{errors.heard_about_us}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">What do you hope to learn?</label>
                      <textarea className="form-control" rows={3} value={form.hope_to_learn} onChange={e => set('hope_to_learn', e.target.value)} placeholder="Optional — tell us your goals for attending." />
                    </div>
                  </>
                )}

                <div className="reg-actions">
                  <button className="pub-btn pub-btn-ghost reg-btn-ghost" onClick={() => setStep(emailExists ? 0 : 1)}>← Back</button>
                  <button className="pub-btn pub-btn-primary reg-btn" onClick={handleRegister} disabled={busy}>
                    {busy ? <span className="spinner spinner-sm" /> : event?.is_free ? 'Complete Registration →' : 'Proceed to Payment →'}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3 — Free confirmation */}
            {isFreeConfirm && (
              <div className="reg-pane reg-success">
                <div className="reg-success-icon">✓</div>
                <h2>You're registered!</h2>
                <p className="reg-sub">
                  Thanks {form.firstname}! Your spot for <strong>{event.title}</strong> is confirmed.
                  We've sent a confirmation to <strong>{email}</strong> and a text to your phone.
                </p>
                <div className="reg-success-actions">
                  <button className="pub-btn pub-btn-primary reg-btn" onClick={() => navigate('/')}>Browse More Events</button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
